/* eslint-disable react/forbid-component-props, sonarjs/cognitive-complexity */
import { Formik, Form } from 'formik';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { useContext, useState, useRef } from 'react';
import { object, string } from 'yup';
import { SOLD_OUT_BANNER_COMPONENT } from '../../brands/default/content/components/deal';
import { setSubscribedBrandWinCookie } from '../../helpers/cookieSetter';
import ErrorTrackingService from '../../helpers/errorTrackingService';
import { notifyUser } from '../../helpers/subscribe';
import ThemeContext from '../../providers/ThemeProvider';
import Button from '../_generic/button/CTAButton';
import ConfirmationModal from '../_generic/confirmation/ConfirmationModal';
import InputForm from '../_generic/input/Input';
import Spinner from '../_generic/spinner/Spinner';
import useToast from '../_generic/toast/UseToast';

const schema = object().shape({
  email: string()
    .required(SOLD_OUT_BANNER_COMPONENT.requiredField)
    .email(SOLD_OUT_BANNER_COMPONENT.enterValidEmail),
});

const SoldOutBanner = ({
  isExpand,
  id = 0,
  dailyPurchaseCapReached = false,
  weeklyPurchaseCapReached = false,
  products = [],
  isProductPage = false,
}) => {
  const ref = useRef(null);
  const toast = useToast();
  const theme = useContext(ThemeContext);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  let bannerText = SOLD_OUT_BANNER_COMPONENT.bannerText;
  const productDailyCap = products?.filter(
    (product) => !product.dailyPurchaseCapReached,
  );
  const productWeeklyCap = products?.filter(
    (product) => !product.weeklyPurchaseCapReached,
  );
  if (productDailyCap?.length === 0)
    bannerText = SOLD_OUT_BANNER_COMPONENT.bannerTextDay;
  if (productWeeklyCap?.length === 0)
    bannerText = SOLD_OUT_BANNER_COMPONENT.bannerTextWeek;
  if (dailyPurchaseCapReached)
    bannerText = SOLD_OUT_BANNER_COMPONENT.bannerTextDay;
  if (weeklyPurchaseCapReached)
    bannerText = SOLD_OUT_BANNER_COMPONENT.bannerTextWeek;
  const isMainDealClosed = (event) => {
    event.preventDefault();
    if (isExpand) isExpand(true);
  };
  const subscribe = async () => {
    setLoading(true);
    setShowConfirmationModal(false);
    try {
      const result = await notifyUser(email, id);
      if (result.status === 201) {
        toast.addToast(
          SOLD_OUT_BANNER_COMPONENT.subscriptionSuccessful,
          'toast-success',
        );
        // BRAND-win (wowcher-win or livingsocial-win)
        setSubscribedBrandWinCookie();
      } else {
        toast.addToast(
          SOLD_OUT_BANNER_COMPONENT.subscriptionFail,
          'toast-error',
        );
      }
    } catch (error) {
      toast.addToast(
        error.message || SOLD_OUT_BANNER_COMPONENT.subscriptionFail,
        'toast-error',
      );
      ErrorTrackingService.logError(error);
    }
    setLoading(false);
    setEmail('');
  };

  return (
    <>
      <div className="banner">
        <div className="banner__pin" />
        <div className="banner__block text-center">
          {loading && !isProductPage && <Spinner hasMsg={false} />}
          <h4 className="banner__title">
            {SOLD_OUT_BANNER_COMPONENT.bannerTitle}
          </h4>
          <p className="banner__text">{bannerText}</p>
          <Formik
            enableReinitialize
            initialValues={{ email }}
            onSubmit={async (values) => {
              setEmail(values.email);
              setShowConfirmationModal(true);
            }}
            validationSchema={schema}
          >
            {({ handleChange, handleBlur, values, errors }) => (
              <Form className="banner__form">
                <InputForm
                  bottomLined
                  error={Boolean(errors.email)}
                  extraClasses="banner__input"
                  inputValue={values.email}
                  name="email"
                  onBlurFunction={handleBlur}
                  onChangeFunction={handleChange}
                  placeholder={SOLD_OUT_BANNER_COMPONENT.bannerPlaceholder}
                  type="email"
                  valid={!errors.email}
                />
                <Button
                  className="banner__button"
                  disabled={Boolean(errors.email)}
                  type="submit"
                >
                  {SOLD_OUT_BANNER_COMPONENT.bannerButtonText}
                </Button>
                {!isProductPage && (
                  <Link href="#">
                    <a
                      className="banner__link banner__link--top"
                      onClick={isMainDealClosed}
                      onKeyDown={isMainDealClosed}
                      role="link"
                      tabIndex={0}
                    >
                      {SOLD_OUT_BANNER_COMPONENT.bannerShowLink}
                    </a>
                  </Link>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
      {showConfirmationModal && (
        <ConfirmationModal
          currentRef={ref.current}
          setShowConfirmationModal={setShowConfirmationModal}
          showConfirmationModal={showConfirmationModal}
          subscribe={subscribe}
        />
      )}

      <style jsx>{`
        .banner {
          padding-top: 40px;
          position: absolute;
          left: 0;
          right: 0;
          top: 6px;
          z-index: 999;
        }
        .banner__pin {
          background: ${theme.colors.primarypale};
          border-radius: 50%;
          height: 15px;
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-5px);
          margin: 0 auto;
          width: 15px;
        }
        .banner__pin:after,
        .banner__pin:before {
          content: '';
          border-top: 5px solid ${theme.colors.primarypale};
          position: absolute;
          width: 160px;
          transform-origin: 0% 0%;
          left: 10px;
          top: 5px;
        }
        .banner__pin:after {
          transform: rotate(15deg);
          top: 1px;
        }
        .banner__pin:before {
          transform: rotate(165deg);
        }
        .banner__block {
          background-color: ${theme.colors.primary};
          color: ${theme.commoncolors.white};
          font-family: 'Open Sans', sans-serif;
          padding: 10px 5px 11px;
          position: relative;
          z-index: 1;
          
        }
        .banner__title {
          display: inline-block;
          position: relative;
          font-weight: 800;
        }
        .banner__title:after {
          content: '';
          background: ${theme.commoncolors.white};
          height: 1px;
          position: absolute;
          left: 0;
          right: 0;
          bottom: 1px;
        }
        .banner__text {
          font-size: 12px;
        }
        .banner__link {
          color: ${theme.commoncolors.white};
          font-size: 10px;
          position: absolute;
          right: 0;
          bottom: 5px;
          max-width: 50px;
          text-align: center;
          text-decoration: underlined;
        }
        :global(.banner__form) {
          position: relative;
        }
        :global(.banner .banner__button) {
          min-height: 35px;
          min-width: 158px;
          margin-bottom: 0;
          background-color: ${theme.colors.dealviewbutton};
          font-size: 18px;
          border-radius: 2rem;
          border: none;
          padding: 7px 10px;
        }
        :global(.banner .banner__button:hover) {
          background-color: ${theme.colors.dealviewbutton};
          border: none;
        }
        :global(.banner .banner__input) {
          margin-bottom: 11px;
        }
        :global(.banner .field__email) {
          padding: 8px 14px;
        }
        @media (min-width: ${theme.breakpoints.mdUp}) {
          .banner {
            padding-top: 100px;
            left: ${isProductPage ? '60px' : '190px'}
            right: 42px;
          }
          .banner__pin {
            background: ${theme.colors.primarypale};
            height: 26px;
            transform: translateX(-13px);
            width: 26px;
          }
          .banner__pin:after,
          .banner__pin:before {
            border-top: 8px solid ${theme.colors.primarypale};
            width: 250px;
          }
          .banner__pin:before {
            top: 10px;
            transform: rotate(150deg);
          }
          .banner__pin:after {
            top: 0;
            left: 14px;
            transform: rotate(30deg);
          }
          .banner__block {
            padding: 10px 20px 11px;
          }
          .banner__title {
            font-size: 20px;
          }
          .banner__title:after {
            bottom: 3px;
          }
          .banner__text {
            font-size: 16px;
          }
          .banner__link {
            font-size: 12px;
            max-width: 100%;
          }
        }
      `}</style>
    </>
  );
};

SoldOutBanner.propTypes = {
  dailyPurchaseCapReached: PropTypes.bool,
  id: PropTypes.number,
  isExpand: PropTypes.func,
  isProductPage: PropTypes.bool,
  products: PropTypes.array,
  weeklyPurchaseCapReached: PropTypes.bool,
};

export default SoldOutBanner;
